<template>
  <div class="p-6 bg-slate-50 min-h-screen">
    
    <!-- 1. Organization Header Card -->
    <div class="w-full mb-8">
      <div class="bg-white rounded-xl border border-gray-200 shadow-sm relative overflow-hidden">
        
        <!-- Setup Banner -->
        <div 
          class="w-full px-6 py-3 flex items-center justify-between cursor-pointer transition-colors border-b"
          :class="isOnboardingComplete
            ? 'bg-green-50 border-green-100 hover:bg-green-100 text-green-800' 
            : 'bg-blue-50 border-blue-100 hover:bg-blue-100 text-blue-800'"
          @click="goToOnboarding"
        >
          <div class="flex items-center gap-3">
            <!-- Fixed Icon Alignment -->
            <div class="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full" 
              :class="isOnboardingComplete ? 'bg-green-200 text-green-700' : 'bg-blue-200 text-blue-700'">
              <feather-icon :icon="isOnboardingComplete ? 'CheckIcon' : 'InfoIcon'" class="w-4 h-4" />
            </div>
            
            <div class="flex flex-col md:flex-row md:items-center gap-1 md:gap-2">
              <span class="font-bold">
                {{ isOnboardingComplete ? 'All set!' : 'Setup In Progress:' }}
              </span>
              <span class="text-sm md:text-base opacity-90">
                {{ isOnboardingComplete
                  ? 'Your organization profile is 100% complete.' 
                  : `You are on Step ${onboardingStep} of 4. Complete your profile to unlock full features.` 
                }}
              </span>
            </div>
          </div>
          <div class="flex items-center gap-1 text-sm font-bold whitespace-nowrap pl-4">
            <span>{{ isOnboardingComplete ? 'View Details' : 'Continue Setup' }}</span>
            <feather-icon icon="ArrowRightIcon" class="w-4 h-4" />
          </div>
        </div>

        <!-- Card Content -->
        <div class="p-6 md:p-8 relative">
          <div v-if="orgData" class="flex flex-col md:flex-row items-center md:items-start gap-8 z-10 relative">
            
            <!-- Logo -->
            <div class="flex-shrink-0">
              <vs-avatar v-if="orgData.logo" class="shadow-md ring-4 ring-gray-50" size="140px"
                :src="constants.s3Bucket + '/organization/logo/' + orgData.logo" />
              <vs-avatar v-else class="shadow-md ring-4 ring-gray-50" size="140px" 
                :src="require('@/assets/images/icons/organization/business_black_36dp.svg')" />
            </div>

            <!-- Info -->
            <div class="flex flex-col items-center md:items-start gap-2 w-full mt-2">
              <div class="flex items-center gap-2 text-gray-500 font-medium mb-1">
                <span>👋</span>
                <span>{{ $t('orgDashboard.organization.welcomeBack') }}</span>
              </div>

              <h1 class="text-4xl font-bold text-gray-800 tracking-tight">{{ orgData.name }}</h1>
              
              <div class="flex items-center gap-4 text-gray-500 mt-1">
                <div class="flex items-center gap-2">
                  <feather-icon icon="GlobeIcon" class="w-4 h-4" />
                  <a 
      v-if="orgData.website"
      :href="formattedWebsiteUrl" 
      target="_blank" 
      rel="noopener noreferrer"
      class="hover:text-blue-600 transition-colors font-medium"
    >
      {{ orgData.website }}
    </a>
    <span v-else class="text-gray-400 cursor-not-allowed">Add website link</span></div>
                <div v-if="orgData.website" class="h-4 w-px bg-gray-300"></div>
                <div v-if="orgData.website" class="cursor-pointer hover:text-gray-800 flex items-center gap-1 group" @click.stop="copyText">
                  <feather-icon :icon="isTextCopied ? 'CheckIcon' : 'CopyIcon'" class="w-4 h-4 group-hover:scale-110 transition-transform" />
                  <span class="text-sm font-medium">{{ isTextCopied ? 'Copied!' : 'Copy' }}</span>
                </div>
              </div>
            </div>
          </div>
          <div class="absolute top-0 right-0 -mt-4 -mr-4 w-64 h-64 bg-gradient-to-bl from-blue-50 to-transparent rounded-full opacity-50 pointer-events-none"></div>
        </div>
      </div>
    </div>

    <!-- 2. KPI Stats Grid -->
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
      
      <!-- Talent Pool Card -->
      <div 
        class="bg-white p-6 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer transform hover:-translate-y-1"
        @click="goToTalentPool"
      >
        <div class="flex justify-between items-start mb-4">
          <div>
            <p class="text-gray-500 text-sm font-medium uppercase tracking-wider">Talent Pool</p>
            <h3 class="text-3xl font-bold text-gray-800 mt-1">{{ metrics.talentPool }}</h3>
          </div>
          <div class="p-3 bg-blue-50 rounded-lg text-blue-600 flex items-center justify-center h-12 w-12">
            <feather-icon icon="UsersIcon" class="w-6 h-6" />
          </div>
        </div>
        <div class="text-sm text-blue-600 font-medium flex items-center gap-1">
          View Candidates <feather-icon icon="ArrowRightIcon" class="w-3 h-3" />
        </div>
      </div>

      <!-- Key Roles Card -->
      <div 
        class="bg-white p-6 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer transform hover:-translate-y-1"
        @click="goToTalentPool"
      >
        <div class="flex justify-between items-start mb-4">
          <div>
            <p class="text-gray-500 text-sm font-medium uppercase tracking-wider">Key Roles</p>
            <h3 class="text-3xl font-bold text-gray-800 mt-1">{{ metrics.keyRoles }}</h3>
          </div>
          <div class="p-3 bg-purple-50 rounded-lg text-purple-600 flex items-center justify-center h-12 w-12">
            <feather-icon icon="BriefcaseIcon" class="w-6 h-6" />
          </div>
        </div>
        <div class="text-sm text-purple-600 font-medium">
          Across Organization
        </div>
      </div>

      <!-- Active Plans Card -->
      <div 
        class="bg-white p-6 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer transform hover:-translate-y-1"
        @click="goToBoards"
      >
        <div class="flex justify-between items-start mb-4">
          <div>
            <p class="text-gray-500 text-sm font-medium uppercase tracking-wider">Active Plans</p>
            <h3 class="text-3xl font-bold text-gray-800 mt-1">{{ metrics.activePlans }}</h3>
          </div>
          <div class="p-3 bg-orange-50 rounded-lg text-orange-600 flex items-center justify-center h-12 w-12">
            <feather-icon icon="LayersIcon" class="w-6 h-6" />
          </div>
        </div>
        <div class="text-sm text-orange-600 font-medium flex items-center gap-1">
          View Boards <feather-icon icon="ArrowRightIcon" class="w-3 h-3" />
        </div>
      </div>

      <!-- Completed Plans Card -->
      <div 
        class="bg-white p-6 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer transform hover:-translate-y-1"
        @click="goToBoards"
      >
        <div class="flex justify-between items-start mb-4">
          <div>
            <p class="text-gray-500 text-sm font-medium uppercase tracking-wider">Completed Plans</p>
            <h3 class="text-3xl font-bold text-gray-800 mt-1">{{ metrics.completedPlans }}</h3>
          </div>
          <div class="p-3 bg-green-50 rounded-lg text-green-600 flex items-center justify-center h-12 w-12">
            <feather-icon icon="CheckCircleIcon" class="w-6 h-6" />
          </div>
        </div>
        <div class="text-sm text-green-600 font-medium">
          Archived/Finished
        </div>
      </div>
    </div>

    <!-- 3. Insights Section -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
      
      <!-- Role Distribution Insight -->
      <div class="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
        <h3 class="text-lg font-bold text-gray-800 mb-6 flex items-center gap-2">
          <feather-icon icon="BarChart2Icon" class="w-5 h-5 text-gray-400" />
          Top Role Distribution
        </h3>
        
        <div v-if="topRoles.length > 0" class="space-y-4">
          <div v-for="(role, index) in topRoles" :key="index" class="relative">
            <div class="flex justify-between text-sm mb-1">
              <span class="font-medium text-gray-700">{{ role.title }}</span>
              <span class="text-gray-500">{{ role.count }} employees</span>
            </div>
            <div class="w-full bg-gray-100 rounded-full h-2.5">
              <div class="bg-blue-500 h-2.5 rounded-full transition-all duration-1000" 
                   :style="{ width: (role.count / metrics.talentPool) * 100 + '%' }"></div>
            </div>
          </div>
        </div>
        <div v-else class="flex flex-col items-center justify-center py-8 text-gray-400">
          <feather-icon icon="UsersIcon" class="w-12 h-12 mb-2 opacity-50" />
          <p>No role data available yet.</p>
        </div>
      </div>

      <!-- Plans Velocity Insight -->
      <div class="bg-white rounded-xl border border-gray-200 shadow-sm p-6 flex flex-col justify-between">
        <div>
          <h3 class="text-lg font-bold text-gray-800 mb-2 flex items-center gap-2">
            <feather-icon icon="ActivityIcon" class="w-5 h-5 text-gray-400" />
            Plans Velocity
          </h3>
          <p class="text-gray-500 text-sm mb-8">Comparison of active vs completed strategic plans.</p>
          
          <div class="flex items-center justify-center gap-8 mb-8">
             <div class="text-center">
                <div class="text-4xl font-bold text-gray-800">{{ metrics.activePlans }}</div>
                <div class="text-xs uppercase tracking-wide text-gray-500 mt-1">Active</div>
             </div>
             <div class="h-12 w-px bg-gray-200"></div>
             <div class="text-center">
                <div class="text-4xl font-bold text-green-600">{{ metrics.completedPlans }}</div>
                <div class="text-xs uppercase tracking-wide text-green-600 mt-1">Completed</div>
             </div>
          </div>
        </div>

        <!-- Visual Bar -->
        <div>
           <div class="w-full bg-gray-100 rounded-lg h-4 overflow-hidden flex">
              <div class="bg-orange-400 h-full" :style="{ width: planVelocityPercentage + '%' }"></div>
              <div class="bg-green-500 h-full" :style="{ width: (100 - planVelocityPercentage) + '%' }"></div>
           </div>
           <div class="flex justify-between mt-2 text-xs text-gray-500 font-medium">
              <span>{{ Math.round(planVelocityPercentage) }}% Active</span>
              <span>{{ Math.round(100 - planVelocityPercentage) }}% Completed</span>
           </div>
        </div>
      </div>
    </div>

    <!-- 4. Quick Links Footer -->
    <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
        <!-- KVDR Reports Card -->
        <div class="bg-white border border-gray-200 rounded-xl p-6 text-gray-800 shadow-sm cursor-pointer transform transition hover:-translate-y-1 hover:shadow-md" @click="goToReports">
          <div class="flex items-center justify-between mb-4">
             <!-- Icon: Blue Accent on White -->
             <div class="bg-blue-50 p-2 rounded-lg text-blue-600 flex items-center justify-center h-10 w-10">
                <feather-icon icon="PieChartIcon" class="w-6 h-6" />
             </div>
             <feather-icon icon="ArrowRightIcon" class="w-5 h-5 text-gray-400" />
          </div>
          <h4 class="text-xl font-bold mb-1">KVDR Reports</h4>
          <p class="text-gray-500 text-sm">Generate insights and download visual reports.</p>
        </div>

        <!-- Talent Explorer Card -->
        <div class="bg-white border border-gray-200 rounded-xl p-6 text-gray-800 shadow-sm cursor-pointer transform transition hover:-translate-y-1 hover:shadow-md" @click="goToTalentPool">
           <div class="flex items-center justify-between mb-4">
             <!-- Icon: Purple Accent on White -->
             <div class="bg-purple-50 p-2 rounded-lg text-purple-600 flex items-center justify-center h-10 w-10">
                <feather-icon icon="UsersIcon" class="w-6 h-6" />
             </div>
             <feather-icon icon="ArrowRightIcon" class="w-5 h-5 text-gray-400" />
          </div>
          <h4 class="text-xl font-bold mb-1">Talent Explorer</h4>
          <p class="text-gray-500 text-sm">Manage your {{ metrics.talentPool }} candidates and employees.</p>
        </div>
    </div>

  </div>
</template>

<script>
import constants from "@/constants.js";

export default {
  data() {
    return {
      constants,
      onboardingStep: 1, // Default to 1
      isTextCopied: false,
      // Metrics Container
      metrics: {
        talentPool: 0,
        keyRoles: 0,
        activePlans: 0,
        completedPlans: 0,
      },
      topRoles: [] // For the distribution chart
    };
  },
  computed: {
    orgData() {
      return this.$store.state.organization.orgData;
    },
    formattedWebsiteUrl() {
      const url = this.orgData?.website;
      if (!url) return '#';
      
      // Check if URL starts with http:// or https://
      if (url.match(/^https?:\/\//)) {
        return url;
      }
      
      // If not, prepend https://
      return `https://${url}`;
    },
    // FIX: Check if step > 4 (means process is finished)
    isOnboardingComplete() {
      return this.onboardingStep > 4;
    },
    planVelocityPercentage() {
      const total = this.metrics.activePlans + this.metrics.completedPlans;
      if (total === 0) return 50; // Default balanced view if no data
      return (this.metrics.activePlans / total) * 100;
    }
  },
  watch: {
    orgData: {
      immediate: true,
      deep: true,
      handler(newVal) {
        if (newVal && newVal._id) {
          // Initialize data requiring Org ID
          this.initDashboardData(newVal._id);
        }
        if (newVal && newVal.slug) {
          // FIX: Load step specifically using Slug immediately
          this.loadOnboardingStep();
        }
      }
    }
  },
  methods: {
    initDashboardData(orgId) {
      this.fetchBoardData(orgId);
      this.fetchEmployeeData(orgId);
    },

    // --- 1. Fetch Boards (Plans) ---
    fetchBoardData(orgId) {
      // Fetch Active Boards
      this.$store.dispatch("boards/fetchAllBoards", orgId).then(() => {
        const activeBoards = this.$store.state.boards.allBoards || [];
        this.metrics.activePlans = activeBoards.length;
      }).catch(console.error);

      // Fetch Archived Boards (Completed Plans)
      this.$store.dispatch("boards/fetchAllArchivedBoards", orgId).then(() => {
         const archivedBoards = this.$store.state.boards.allArchivedBoards || [];
         this.metrics.completedPlans = archivedBoards.length;
      }).catch(console.error);
    },

    // --- 2. Fetch Employees (Talent Pool & Roles) ---
    fetchEmployeeData(orgId) {
      this.$store.dispatch("organization/getAllOrgEmployees", orgId).then((res) => {
        if (res && res.data) {
          const employees = res.data;
          
          // A. Talent Pool Count
          this.metrics.talentPool = employees.length;

          // B. Role Analysis
          const roleMap = {};
          employees.forEach(e => {
             const title = e.roleTitle ? e.roleTitle.trim() : 'Unassigned';
             if (title && title !== 'GENERAL') {
                roleMap[title] = (roleMap[title] || 0) + 1;
             }
          });

          // Count Unique Roles
          this.metrics.keyRoles = Object.keys(roleMap).length;

          // C. Calculate Top Roles for Chart
          this.topRoles = Object.entries(roleMap)
            .map(([title, count]) => ({ title, count }))
            .sort((a, b) => b.count - a.count)
            .slice(0, 5); // Top 5
        }
      }).catch(console.error);
    },

    // --- Navigation & Utilities ---
    goToOnboarding() {
      if (this.orgData && this.orgData.slug) {
        this.$router.push(`/organization/${this.orgData.slug}/onboarding`);
      }
    },
    goToTalentPool() {
      if (this.orgData && this.orgData.slug) {
        this.$router.push(`/organization/${this.orgData.slug}/talent-pool`);
      }
    },
    goToReports() {
      if (this.orgData && this.orgData.slug) {
        this.$router.push(`/organization/${this.orgData.slug}/reports`);
      }
    },
    goToBoards() {
      if (this.orgData && this.orgData.slug) {
       this.$router.push(`/organization/${this.orgData.slug}/boards`);
      }
    },
    async copyText() {
      try {
        await navigator.clipboard.writeText(this.orgData.website);
        this.isTextCopied = true;
        setTimeout(() => (this.isTextCopied = false), 2000);
      } catch (err) {
        console.error("Failed to copy text: ", err);
      }
    },
    
    // FIX: Method to load organization-specific key
    loadOnboardingStep() {
      if (this.orgData && this.orgData.slug) {
        const key = `onboardingStep_${this.orgData.slug}`;
        const savedStep = localStorage.getItem(key);
        
        if (savedStep) {
          this.onboardingStep = parseInt(savedStep);
        } else {
          this.onboardingStep = 1; // Default if not found
        }
      }
    }
  }
};
</script>

<style scoped>
.hover\:shadow-md:hover {
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}
</style>